import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  alpha,
  useTheme,
  Paper
} from '@mui/material';
import {
  History as HistoryIcon,
  Assignment as AssignmentIcon,
  Feedback as FeedbackIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  CalendarToday as CalendarTodayIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Star as StarIcon,
  Code as CodeIcon,
  Work as WorkIcon,
  Psychology as PsychologyIcon
} from '@mui/icons-material';
import { getRatingData, getUserRatingResults } from '../Queries';

const RatingHistoryDialog = ({ 
  open, 
  onClose, 
  selectedUser 
}) => {
  const theme = useTheme();
  const [userHistory, setUserHistory] = useState([]);
  const [expandedAccordion, setExpandedAccordion] = useState(null);
  const [loading, setLoading] = useState(false);

  // Get user's complete rating history when dialog opens
  useEffect(() => {
    const loadUserRatingHistory = async () => {
      if (selectedUser && open) {
        setLoading(true);
        console.log('Loading rating history for user:', selectedUser);
        
        try {
          // Get all rating forms
          const allRatingForms = await getRatingData();
          
          // Get user's rating submissions
          const userRatings = await getUserRatingResults(selectedUser.email || selectedUser.id);
          
          // Combine form data with user submissions
          const userRatingHistory = [];
          
          if (userRatings && userRatings.userRatingResults) {
            for (const rating of userRatings.userRatingResults) {
              // Handle DynamoDB format
              const ratingUserId = rating.user_id?.S || rating.user_id;
              const ratingParentId = rating.parent_id?.S || rating.parent_id;
              const ratingData = rating.rating_data?.S || rating.rating_data;
              const createdAt = rating.created_at?.S || rating.created_at;
              
              // Find the corresponding form
              const correspondingForm = allRatingForms.find(form => 
                form.form_id.toString() === ratingParentId
              );
              
              if (correspondingForm && ratingUserId === (selectedUser.email || selectedUser.id)) {
                userRatingHistory.push({
                  ...correspondingForm,
                  userRating: rating,
                  ratingData: ratingData ? JSON.parse(ratingData) : null,
                  submittedAt: createdAt,
                  ratingId: rating.id?.S || rating.id
                });
              }
            }
          }

          // Group by form_id to handle multiple entries for the same form
          const groupedByForm = {};
          userRatingHistory.forEach(rating => {
            const formId = rating.form_id;
            if (!groupedByForm[formId]) {
              groupedByForm[formId] = [];
            }
            groupedByForm[formId].push(rating);
          });

          // For each form, select the best entry (prioritizing in-progress/active status)
          const uniqueHistory = [];
          Object.values(groupedByForm).forEach(formRatings => {
            if (formRatings.length === 1) {
              uniqueHistory.push(formRatings[0]);
            } else {
              // Sort by status priority: Active > Completed > Others
              const prioritized = formRatings.sort((a, b) => {
                const getStatusPriority = (startDate, endDate) => {
                  const currentDate = new Date();
                  const formattedStartDate = new Date(startDate);
                  const formattedEndDate = new Date(endDate);
                  
                  if (currentDate >= formattedStartDate && currentDate <= formattedEndDate) {
                    return 3; // Active (highest priority)
                  } else if (currentDate > formattedEndDate) {
                    return 2; // Closed/Completed
                  } else {
                    return 1; // Upcoming
                  }
                };
                
                const aPriority = getStatusPriority(a.start_date, a.end_date);
                const bPriority = getStatusPriority(b.start_date, b.end_date);
                
                if (aPriority !== bPriority) {
                  return bPriority - aPriority; // Higher priority first
                }
                
                // If same priority, sort by submission date (newest first)
                return new Date(b.submittedAt || b.created_at) - new Date(a.submittedAt || a.created_at);
              });
              
              uniqueHistory.push(prioritized[0]); // Take the highest priority entry
            }
          });

          // Sort the final unique history by submission date (newest first)
          const sortedHistory = uniqueHistory.sort((a, b) => 
            new Date(b.submittedAt || b.created_at) - new Date(a.submittedAt || a.created_at)
          );

          console.log('Unique user rating history found:', sortedHistory);
          setUserHistory(sortedHistory);
        } catch (error) {
          console.error('Error loading rating history:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadUserRatingHistory();
  }, [selectedUser, open]);

  const getStatusColor = (startDate, endDate) => {
    const currentDate = new Date();
    const formattedStartDate = new Date(startDate);
    const formattedEndDate = new Date(endDate);
    
    if (currentDate < formattedStartDate) {
      return { color: theme.palette.info.main, status: 'Upcoming' };
    } else if (currentDate >= formattedStartDate && currentDate <= formattedEndDate) {
      return { color: theme.palette.success.main, status: 'Active' };
    } else {
      return { color: theme.palette.grey[500], status: 'Closed' };
    }
  };

  const getStatusIcon = (startDate, endDate) => {
    const statusInfo = getStatusColor(startDate, endDate);
    switch (statusInfo.status) {
      case 'Active':
        return <CheckCircleIcon sx={{ fontSize: 20 }} />;
      case 'Upcoming':
        return <ScheduleIcon sx={{ fontSize: 20 }} />;
      case 'Closed':
        return <AssignmentIcon sx={{ fontSize: 20 }} />;
      default:
        return <FeedbackIcon sx={{ fontSize: 20 }} />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'learning_skill_growth':
        return <PsychologyIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />;
      case 'task_ownership_delivery':
        return <AssignmentIcon sx={{ fontSize: 16, color: theme.palette.secondary.main }} />;
      case 'problem_solving_thinking':
        return <TrendingUpIcon sx={{ fontSize: 16, color: theme.palette.info.main }} />;
      case 'code_quality_engineering':
        return <CodeIcon sx={{ fontSize: 16, color: theme.palette.error.main }} />;
      case 'communication_collaboration':
        return <WorkIcon sx={{ fontSize: 16, color: theme.palette.warning.main }} />;
      case 'professional_habits':
        return <StarIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />;
      default:
        return <FeedbackIcon sx={{ fontSize: 16, color: theme.palette.grey[600] }} />;
    }
  };

  const getCategoryName = (category) => {
    const names = {
      'learning_skill_growth': 'Learning & Skill Growth',
      'task_ownership_delivery': 'Task Ownership & Delivery',
      'problem_solving_thinking': 'Problem Solving & Thinking',
      'code_quality_engineering': 'Code Quality & Engineering',
      'communication_collaboration': 'Communication & Collaboration',
      'professional_habits': 'Professional Habits'
    };
    return names[category] || category;
  };

  if (!selectedUser) {
    return null;
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #2196f3 0%, #9c27b0 100%)',
        color: 'white',
        py: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 45, height: 45 }}>
          <HistoryIcon sx={{ fontSize: 24, color: 'white' }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.2rem', mb: 0.5 }}>
            Self-Reflection History
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.9rem' }}>
            {selectedUser.name || selectedUser.email || selectedUser.id}
          </Typography>
        </Box>
        <Button
          onClick={onClose}
          sx={{ 
            color: 'white',
            minWidth: 'auto',
            p: 1
          }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: 300,
            p: 4 
          }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Loading History...
            </Typography>
          </Box>
        ) : userHistory.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: 300,
            p: 4 
          }}>
            <HistoryIcon sx={{ fontSize: 64, color: theme.palette.grey[400], mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No Self-Reflection History
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              This user has not completed any self-reflection forms yet.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: 3 }}>
            {userHistory.map((rating, index) => {
              const statusInfo = getStatusColor(rating.start_date, rating.end_date);
              const questions = typeof rating.questions === 'string' ? JSON.parse(rating.questions) : rating.questions;

              return (
                <Box key={rating.ratingId || index} sx={{ mb: 3, position: 'relative' }}>
                  {/* Timeline connecting line */}
                  {index < userHistory.length - 1 && (
                    <Box 
                      sx={{ 
                        position: 'absolute',
                        left: 20,
                        top: 60,
                        bottom: -24,
                        width: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.3),
                        zIndex: 0
                      }} 
                    />
                  )}
                  
                  <Stack direction="row" spacing={2} sx={{ position: 'relative', zIndex: 1 }}>
                    {/* Timeline status indicator */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80 }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: statusInfo.color,
                          width: 40,
                          height: 40,
                          mb: 1,
                          border: `3px solid ${alpha(statusInfo.color, 0.2)}`
                        }}
                      >
                        {getStatusIcon(rating.start_date, rating.end_date)}
                      </Avatar>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'center', fontWeight: 600 }}>
                        {formatDate(rating.submittedAt)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                        Period: {formatDate(rating.start_date)} - {formatDate(rating.end_date)}
                      </Typography>
                    </Box>
                    
                    {/* Content card */}
                    <Box sx={{ flex: 1 }}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          mb: 2, 
                          border: `1px solid ${alpha(statusInfo.color, 0.3)}`,
                          borderLeft: `4px solid ${statusInfo.color}`,
                          backgroundColor: alpha(statusInfo.color, 0.02)
                        }}
                      >
                        <CardContent>
                          {/* Header */}
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: statusInfo.color }}>
                                {rating.name}
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                Program: {rating.program_code}
                              </Typography>
                              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic' }}>
                                Submitted: {formatDate(rating.submittedAt)}
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={1}>
                              <Chip 
                                label={statusInfo.status} 
                                size="small"
                                sx={{ 
                                  bgcolor: alpha(statusInfo.color, 0.1),
                                  color: statusInfo.color,
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  border: `1px solid ${alpha(statusInfo.color, 0.3)}`
                                }}
                              />
                              {statusInfo.status === 'Active' && (
                                <Chip 
                                  label="In Progress" 
                                  size="small"
                                  sx={{ 
                                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                                    color: theme.palette.warning.main,
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`
                                  }}
                                />
                              )}
                            </Stack>
                          </Box>

                          {/* Rating Categories */}
                          {rating.ratingData && (
                            <Accordion 
                              expanded={expandedAccordion === `rating-${rating.ratingId}-${index}`}
                              onChange={() => setExpandedAccordion(
                                expandedAccordion === `rating-${rating.ratingId}-${index}` ? null : `rating-${rating.ratingId}-${index}`
                              )}
                              sx={{ mb: 1, boxShadow: 'none', border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}
                            >
                              <AccordionSummary 
                                expandIcon={<ExpandMoreIcon />}
                                sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <FeedbackIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                  <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'primary.main', fontWeight: 500 }}>
                                    View Self-Reflection Details
                                  </Typography>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails sx={{ p: 2 }}>
                                <Grid container spacing={2}>
                                  {Object.entries(rating.ratingData).map(([category, data]) => {
                                    if (category.includes('mentor') || !data || typeof data !== 'object') return null;
                                    
                                    return (
                                      <Grid item xs={12} md={6} key={category}>
                                        <Paper 
                                          elevation={0}
                                          sx={{ 
                                            p: 1.5, 
                                            bgcolor: alpha(theme.palette.primary.main, 0.05), 
                                            borderRadius: 1, 
                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` 
                                          }}
                                        >
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            {getCategoryIcon(category)}
                                            <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontWeight: 600, fontSize: '0.75rem' }}>
                                              {getCategoryName(category)}
                                            </Typography>
                                          </Box>
                                          
                                          {data.rating && (
                                            <Box sx={{ mb: 1 }}>
                                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                Self-Rating:
                                              </Typography>
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                  <StarIcon
                                                    key={star}
                                                    sx={{
                                                      fontSize: 16,
                                                      color: star <= parseInt(data.rating) ? '#ffc107' : '#e0e0e0'
                                                    }}
                                                  />
                                                ))}
                                                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, ml: 0.5 }}>
                                                  {data.rating}/5
                                                </Typography>
                                              </Box>
                                            </Box>
                                          )}
                                          
                                          {data.comments && (
                                            <Box>
                                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                Comments:
                                              </Typography>
                                              <Typography variant="body2" sx={{ fontSize: '0.75rem', mt: 0.25, fontStyle: 'italic' }}>
                                                "{data.comments}"
                                              </Typography>
                                            </Box>
                                          )}
                                        </Paper>
                                      </Grid>
                                    );
                                  })}
                                </Grid>
                              </AccordionDetails>
                            </Accordion>
                          )}

                          {/* Questions Preview */}
                          {questions && (
                            <Accordion 
                              expanded={expandedAccordion === `questions-${rating.ratingId}-${index}`}
                              onChange={() => setExpandedAccordion(
                                expandedAccordion === `questions-${rating.ratingId}-${index}` ? null : `questions-${rating.ratingId}-${index}`
                              )}
                              sx={{ boxShadow: 'none', border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }}
                            >
                              <AccordionSummary 
                                expandIcon={<ExpandMoreIcon />}
                                sx={{ bgcolor: alpha(theme.palette.info.main, 0.05) }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <InfoIcon sx={{ fontSize: 18, color: 'info.main' }} />
                                  <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'info.main', fontWeight: 500 }}>
                                    View Form Questions
                                  </Typography>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails sx={{ p: 2 }}>
                                <Grid container spacing={2}>
                                  {Object.entries(questions).map(([category, categoryQuestions]) => {
                                    if (!Array.isArray(categoryQuestions) || categoryQuestions.length === 0) return null;
                                    
                                    return (
                                      <Grid item xs={12} key={category}>
                                        <Box sx={{ mb: 2 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            {getCategoryIcon(category)}
                                            <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary' }}>
                                              {getCategoryName(category)}
                                            </Typography>
                                          </Box>
                                          <List dense sx={{ pl: 2 }}>
                                            {categoryQuestions.slice(0, 3).map((question, qIndex) => (
                                              <ListItem key={qIndex} sx={{ py: 0.25, px: 0 }}>
                                                <ListItemText 
                                                  primary={`• ${question}`}
                                                  primaryTypographyProps={{ 
                                                    fontSize: '0.75rem', 
                                                    color: 'text.secondary' 
                                                  }}
                                                />
                                              </ListItem>
                                            ))}
                                            {categoryQuestions.length > 3 && (
                                              <ListItem sx={{ py: 0.25, px: 0 }}>
                                                <ListItemText 
                                                  primary={`... and ${categoryQuestions.length - 3} more questions`}
                                                  primaryTypographyProps={{ 
                                                    fontSize: '0.75rem', 
                                                    color: 'text.secondary',
                                                    fontStyle: 'italic'
                                                  }}
                                                />
                                              </ListItem>
                                            )}
                                          </List>
                                        </Box>
                                      </Grid>
                                    );
                                  })}
                                </Grid>
                              </AccordionDetails>
                            </Accordion>
                          )}
                        </CardContent>
                      </Card>
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: alpha(theme.palette.grey[100], 0.5) }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ 
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 500,
            minWidth: 120,
            borderRadius: 2,
            borderColor: '#2196f3',
            color: '#2196f3',
            '&:hover': {
              backgroundColor: alpha('#2196f3', 0.05),
              borderColor: '#2196f3'
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RatingHistoryDialog;